/**
 * Human batch numbers (BSP-261007-014). The doc id stays the timestamp and is
 * what API calls, Firestore listeners and item barcodes use. People see, type
 * and scan the number; batch URLs and the pull-sheet barcode carry it too.
 * Batches made before numbering have no `batchNumber` and show their id.
 */
export const BATCH_NUMBER_RE = /^[A-Z0-9]{2,4}-\d{6}-\d{3,}$/;

export const batchDisplayId = (batch, fallbackId = "") => {
  const id = String(batch?.id || "");
  const fallback = String(fallbackId || "");
  // A batch left in state from the previous page must not label this one.
  if (fallback && id && id !== fallback) return fallback;
  return String(batch?.batchNumber || id || fallback);
};

/** `{id}-stocked` → `BSP-261007-014-stocked` when `batch` has a number. */
export const withBatchNumber = (value, batch) => {
  const text = String(value || "");
  const id = String(batch?.id || "");
  if (!batch?.batchNumber || !id || !text.startsWith(id)) return text;
  return `${batch.batchNumber}${text.slice(id.length)}`;
};

export const normalizeBatchNumberInput = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

export const isBatchNumber = (value) =>
  BATCH_NUMBER_RE.test(normalizeBatchNumberInput(value));

export const batchOptionMeta = (batch) => {
  if (!batch) return "";
  const bits = [];
  const count = batch.itemsCount;
  if (typeof count === "number") {
    bits.push(`${count} item${count === 1 ? "" : "s"}`);
  }
  if (batch.archived) bits.push("Archived");
  else if (batch.completed) bits.push("Complete");
  return bits.join(" · ");
};

/** Header search: batch hits first, and drop a stale batches group. */
export const mergeBatchSearchResults = (results, batches) => {
  const list = Array.isArray(results) ? results : [];
  const without = list.filter((row) => row?.collection !== "batches");
  const hits = (batches || []).filter((batch) => batch?.id);
  if (!hits.length) return without;
  return [{ collection: "batches", hits }, ...without];
};

/** `/batches/{batchNumber || id}{suffix}` for links people see. */
export const batchPath = (batch, suffix = "") => {
  const key = String(batch?.batchNumber || batch?.id || "");
  return `/batches/${encodeURIComponent(key)}${suffix}`;
};

/** Replaces the `/batches/{from}` segment of `pathname` with `to`. */
export const swapBatchPathSegment = (pathname, from, to) => {
  const parts = String(pathname || "").split("/");
  const i = parts.indexOf("batches");
  if (i < 0 || i + 1 >= parts.length || !from || !to) return pathname;
  let current = parts[i + 1];
  try {
    current = decodeURIComponent(current);
  } catch (_) {}
  if (current !== String(from)) return pathname;
  parts[i + 1] = encodeURIComponent(String(to));
  return parts.join("/");
};
